import { useQuery } from '@tanstack/react-query';
import { adminDashboard } from '@/api/admin';
import type { DashboardDetachedState } from '@/api/types';

/**
 * 虚实脱钩态的读取与归一。
 *
 * 「真实账户平仓」执行后,全部存量虚拟腿被打上脱钩标记:不再计入真实净仓目标、平仓不发真实单、
 * 按中价结算。此时全站的真实口径(保证金占用 / 净敞口 / 基差 / 对账)都只覆盖仍在对冲的部分,
 * 页面若不点破,管理员会把"真实口径归零"误读成"风险归零"。故各页统一从这里取状态。
 *
 * 三态而非两态(**未知 ≠ 未脱钩**):后端在脱钩聚合查询失败时返回 `detached: null`(而不是
 * `{active:false}`),因为"读不到" ≠ "没有敞口" —— 若把两者都渲染成未脱钩,徽标会在数据源
 * 抖动时静默消失,恰恰是最需要它的时候看不见。故区分:
 * - `unknown=true`:后端返回 null(聚合失败)→ 渲染「脱钩态未知」,不给安全暗示;
 * - `active=true`:确有在持脱钩腿;
 * - 两者皆 false:确认未脱钩。
 *
 * 后端灰度中若整个字段缺省(undefined),按未脱钩渲染 —— 那是"这版后端还没这功能",与"读失败"不同。
 */
export interface DetachState {
  active: boolean;
  since: string | null;
  legs: number;
  /** 后端聚合失败、状态不可知。此时 active/legs 无意义,不得据其做"安全"判断。 */
  unknown: boolean;
}

const DETACHED_NONE: DetachState = { active: false, since: null, legs: 0, unknown: false };
const DETACHED_UNKNOWN: DetachState = { active: false, since: null, legs: 0, unknown: true };

export function normalizeDetachState(
  d: DashboardDetachedState | null | undefined,
): DetachState {
  // null = 后端明确表示"这次没读到";undefined = 后端根本没这个字段(旧版本)。
  if (d === null) return DETACHED_UNKNOWN;
  if (!d || d.active !== true) return DETACHED_NONE;
  return { active: true, since: d.since ?? null, legs: d.legs ?? 0, unknown: false };
}

/**
 * 全局脱钩态。复用 dashboard 的 queryKey(与仪表板/大屏同一份缓存,不额外拉一套数据);
 * 轮询 30s —— 徽标只需"是否脱钩",不必跟仪表板 5s 同频。
 */
export function useDetachState(): DetachState {
  const q = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: adminDashboard.get,
    refetchInterval: 30_000,
  });
  return normalizeDetachState(q.data?.riskLevels?.detached);
}
